// Only explicit user/workflow intent enters this writer. Heartbeats never call it.
export function createStatusIntent({ api, currentSession, notify, refreshCanonical }) {
  let revision = 0, confirmed = 0, submitted = 0, queued = null, running = null;
  let disposed = false, error = '', accepted = null, lastPayload = null;
  const session = currentSession();
  const current = () => !disposed && session && currentSession() === session;
  const publish = () => { if (current()) notify({ revision, pending: revision > confirmed, error }); };
  const drain = () => {
    if (running || !queued || !current()) return;
    running = (async () => {
      while (queued && current()) {
        const next = queued; queued = null;
        try {
          const response = await api.post('/status/ping', next.payload,
            { timeout: 15000, expectedSessionToken: session });
          if (!current()) return;
          if (response?.status !== 200 || !Number.isFinite(Date.parse(response.data?.serverTimeUtc)))
            throw new Error('Status confirmation missing');
          accepted = next;
          try {
            await refreshCanonical();
            if (!current()) return;
            confirmed = next.revision;
            accepted = null; error = '';
          } catch {
            if (!current()) return;
            error = 'Status saved. Dispatch refresh is delayed. Refresh to confirm the current status.';
          }
        } catch {
          if (!current()) return;
          error = 'Status confirmation was not received. Check dispatch before retrying the requested change.';
        }
        publish();
      }
    })().finally(() => { running = null; if (queued && current()) drain(); });
  };
  return {
    activate() { disposed = false; },
    dispose() { disposed = true; queued = null; },
    mark() {
      if (!current()) return;
      revision++; error = ''; publish();
    },
    flush(payload) {
      if (!current() || !payload || submitted === revision || revision <= confirmed) return;
      submitted = revision;
      lastPayload = { ...payload };
      queued = { revision, payload: lastPayload };
      drain();
    },
    async retry() {
      if (!current() || running) return;
      if (accepted && accepted.revision === revision) {
        try {
          await refreshCanonical();
          if (current()) { confirmed = revision; accepted = null; error = ''; publish(); }
        } catch { /* Retain the saved-but-not-refreshed warning; never replay it. */ }
      } else if (lastPayload && revision > confirmed) {
        queued = { revision, payload: { ...lastPayload } }; error = ''; publish(); drain();
      }
    },
    async discard() {
      if (!current() || running) return;
      try {
        await refreshCanonical();
        if (current()) { confirmed = revision; submitted = revision; queued = null; accepted = null; error = ''; publish(); }
      } catch { if (current()) { error = 'Unable to refresh current dispatch status.'; publish(); } }
    },
  };
}
